/**
 * DashboardPage Component
 *
 * Protected dashboard page for authenticated users.
 * Features a therapist chat interface as the primary view with a thread sidebar,
 * plus a collapsible stats/activity section below the chat.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  Clock,
  BarChart2,
  TrendingUp,
  Upload,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { formatDistanceToNow } from "date-fns";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { listReflections } from "../services/reflections";
import { listEntries } from "../services/relationshipEntries";
import type { ReflectionResponse } from "../types/reflection";
import type { RelationshipEntryListResponse } from "../types/relationshipEntry";
import { WelcomeBanner } from "../components/dashboard/WelcomeBanner";
import { GettingStartedChecklist } from "../components/dashboard/GettingStartedChecklist";
import { TherapistThreadList } from "../components/therapist-chat/TherapistThreadList";
import { TherapistThreadChat } from "../components/therapist-chat/TherapistThreadChat";

export function DashboardPage() {
  const navigate = useNavigate();
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [mobileThreadsOpen, setMobileThreadsOpen] = useState(false);

  const handleSelectThread = (id: string) => {
    setSelectedThreadId(id);
    setMobileThreadsOpen(false);
  };

  const { data: reflections, isLoading: reflectionsLoading } = useQuery<
    ReflectionResponse[],
    Error
  >({
    queryKey: ["reflections"],
    queryFn: () => listReflections(),
  });

  const { data: trackerData } = useQuery<RelationshipEntryListResponse, Error>({
    queryKey: ["relationshipEntries", "hasAny"],
    queryFn: () => listEntries("2000-01-01", "2099-12-31"),
  });

  // Calculate stats from reflections
  const totalReflections = reflections?.length ?? 0;
  const thisMonth =
    reflections?.filter((r) => {
      const created = new Date(r.created_at);
      const now = new Date();
      return (
        created.getMonth() === now.getMonth() &&
        created.getFullYear() === now.getFullYear()
      );
    }).length ?? 0;

  // Get recent reflections (last 5)
  const recentReflections = reflections
    ?.filter((r) => r.status === "completed")
    .slice(0, 5);

  // Calculate average duration from reflections that have duration data
  const durationsInSeconds =
    reflections
      ?.map((r) => r.duration)
      .filter((d): d is number => d != null && d > 0) ?? [];
  const avgDurationSeconds =
    durationsInSeconds.length > 0
      ? Math.round(
          durationsInSeconds.reduce((sum, d) => sum + d, 0) /
            durationsInSeconds.length
        )
      : 0;
  const avgDurationDisplay =
    avgDurationSeconds > 0
      ? avgDurationSeconds >= 60
        ? `${Math.round(avgDurationSeconds / 60)}m`
        : `${avgDurationSeconds}s`
      : "--";

  // Calculate total insights found across all reflections
  const totalInsights =
    reflections?.reduce((sum, r) => sum + (r.insight_count ?? 0), 0) ?? 0;

  const stats = [
    {
      label: "Total Reflections",
      value: totalReflections.toString(),
      icon: FileText,
      color: "text-blue-500",
    },
    {
      label: "This Month",
      value: thisMonth.toString(),
      icon: Clock,
      color: "text-green-500",
    },
    {
      label: "Avg. Duration",
      value: avgDurationDisplay,
      icon: BarChart2,
      color: "text-purple-500",
    },
    {
      label: "Insights Found",
      value: totalInsights.toString(),
      icon: TrendingUp,
      color: "text-orange-500",
    },
  ];

  const isLoading = userLoading || reflectionsLoading;

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-4rem)] supports-[height:100dvh]:h-[calc(100dvh-4rem)]">
        {/* Header. Hidden on mobile: the title and greeting are decoration,
            and on a phone they cost ~70px of the chat for no information the
            user does not already have. */}
        <div className="hidden border-b bg-background px-4 py-3 md:block">
          <h1 className="text-xl font-bold text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Welcome back{user?.first_name ? `, ${user.first_name}` : ""}! Here's
            your overview.
          </p>
        </div>

        {/* Welcome banner for new users (0 reflections) */}
        {!reflectionsLoading && totalReflections === 0 && (
          <div className="px-4 pt-3">
            <WelcomeBanner firstName={user?.first_name ?? null} />
          </div>
        )}

        {/* Getting started checklist for new users */}
        {!reflectionsLoading && (
          <div className="px-4">
            <GettingStartedChecklist
              hasReflections={totalReflections > 0}
              hasTrackerEntries={(trackerData?.total_count ?? 0) > 0}
            />
          </div>
        )}

        {/* Chat interface — primary feature */}
        <div className="flex flex-1 min-h-0">
          {/* Thread sidebar — desktop only.
              The overdue check-in nudge used to sit above the thread list here
              (and as a compact row on mobile). It was navigation, not dashboard
              content — every row linked to /growth — so it now lives as a count
              badge on the Growth nav item, which reaches every page instead of
              just this one. See OverdueGrowthBadge. */}
          <div className="w-[280px] border-r bg-background shrink-0 hidden md:flex md:flex-col">
            <div className="flex-1 min-h-0">
              <TherapistThreadList
                selectedThreadId={selectedThreadId}
                onSelectThread={setSelectedThreadId}
                onThreadDeleted={(deletedId) => {
                  if (selectedThreadId === deletedId) {
                    setSelectedThreadId(null);
                  }
                }}
              />
            </div>
          </div>

          {/* Chat area. min-h-0 matters: without it this flex item keeps
              min-height:auto and refuses to shrink below its content, which
              leaves the chat's scroll container with nothing to scroll. */}
          <div className="flex-1 min-w-0 min-h-0">
            <TherapistThreadChat
              threadId={selectedThreadId}
              onThreadDeleted={() => setSelectedThreadId(null)}
              onOpenThreadList={() => setMobileThreadsOpen(true)}
            />
          </div>
        </div>

        {/* Mobile: thread picker dialog */}
        <Dialog open={mobileThreadsOpen} onOpenChange={setMobileThreadsOpen}>
          <DialogContent className="max-w-md p-0 gap-0 h-[80vh] flex flex-col">
            <DialogHeader className="px-4 py-3 border-b shrink-0">
              <DialogTitle className="text-base">Conversations</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-hidden">
              <TherapistThreadList
                selectedThreadId={selectedThreadId}
                onSelectThread={handleSelectThread}
                onThreadDeleted={(deletedId) => {
                  if (selectedThreadId === deletedId) {
                    setSelectedThreadId(null);
                  }
                }}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Collapsible stats and activity section */}
        <div className="border-t bg-background">
          <button
            onClick={() => setShowStats(!showStats)}
            className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <span>Stats & Recent Activity</span>
            {showStats ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </button>

          {showStats && (
            <div className="px-4 pb-4 max-h-[50vh] overflow-y-auto">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                {stats.map((stat) => (
                  <Card key={stat.label} className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <stat.icon className={`w-4 h-4 ${stat.color}`} />
                      <span className="text-xs text-muted-foreground">
                        {stat.label}
                      </span>
                    </div>
                    <div className="text-lg font-bold text-foreground">
                      {isLoading ? (
                        <div className="h-6 w-8 bg-muted animate-pulse rounded" />
                      ) : (
                        stat.value
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="grid md:grid-cols-2 gap-3 mb-4">
                <Card
                  className="p-4 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate("/reflections")}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Upload className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">
                        Upload New Recording
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Start a new reflection by uploading audio
                      </p>
                    </div>
                  </div>
                </Card>

                <Card
                  className="p-4 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate("/reflections")}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">
                        View All Reflections
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Browse past conversations and insights
                      </p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Recent Activity */}
              <Card className="p-4">
                <h2 className="text-sm font-semibold text-foreground mb-3">
                  Recent Activity
                </h2>

                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : recentReflections && recentReflections.length > 0 ? (
                  <div className="space-y-2">
                    {recentReflections.map((reflection) => (
                      <div
                        key={reflection.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() =>
                          navigate(`/reflections/${reflection.id}`)
                        }
                      >
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-foreground truncate">
                            {reflection.title || "Untitled Reflection"}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {reflection.therapy_style && (
                              <>{reflection.therapy_style.name} &middot; </>
                            )}
                            {formatDistanceToNow(
                              new Date(reflection.created_at),
                              { addSuffix: true }
                            )}
                          </p>
                        </div>
                        {reflection.therapy_style && (
                          <Badge
                            variant="secondary"
                            className="hidden sm:flex text-xs"
                          >
                            {reflection.therapy_style.name}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/reflections/${reflection.id}`);
                          }}
                        >
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-2">
                      No reflections yet
                    </p>
                    <Button size="sm" onClick={() => navigate("/reflections")}>
                      Upload your first reflection
                    </Button>
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
